/**
 * Modale "Générer un document" — sur fiche employé.
 * Choix du modèle → saisie variables manquantes → preview → téléchargement.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { FileText, Download, X, Loader2, Eye } from 'lucide-react';
import {
  getDocumentTemplates,
  getDocumentTemplateVariables,
  generateEmployeeDocument,
  previewDocumentTemplate,
} from '../../../api';
import { useToast } from '../../../contexts/NotificationContext';

const OVERRIDE_FIELDS = [
  { key: 'contract.trialPeriodMonths', label: 'Période d\'essai (mois)', type: 'number' },
  { key: 'contract.trialEndDate', label: 'Fin de période d\'essai', type: 'date' },
  { key: 'contract.endDate', label: 'Date de fin (CDD)', type: 'date' },
];

export default function GenerateDocumentModal({ employee, onClose, onGenerated }) {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [generating, setGenerating] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [tpl, vars] = await Promise.all([
          getDocumentTemplates({ includeInactive: false }),
          getDocumentTemplateVariables(),
        ]);
        setTemplates(tpl.data || []);
        setVariables(vars.data || []);
        if (tpl.data?.length) setSelectedId(tpl.data[0].id);
      } catch {
        toast.error('Erreur de chargement des modèles');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId),
    [templates, selectedId]
  );

  const handlePreview = async () => {
    if (!selectedId) return;
    try {
      const res = await previewDocumentTemplate({
        templateId: selectedId,
        employeeId: employee.id,
        overrides,
      });
      setPreviewHtml(res.data);
    } catch {
      toast.error('Erreur lors de l\'aperçu');
    }
  };

  const handleGenerate = async () => {
    if (!selectedId) return;
    setGenerating(true);
    try {
      const res = await generateEmployeeDocument(employee.id, {
        templateId: selectedId,
        overrides,
      });
      // Téléchargement immédiat
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selected?.title || 'document'} - ${employee.firstName} ${employee.lastName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Document généré');
      if (onGenerated) onGenerated();
      onClose();
    } catch {
      toast.error('Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileText size={18} className="text-primary" />
            Générer un document — {employee.firstName} {employee.lastName}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 size={20} className="animate-spin text-primary" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-10 text-gray-500 text-sm">
              Aucun modèle disponible.
              <br />
              Crée-en un depuis « RH → Modèles de documents ».
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Modèle
                </label>
                <select
                  value={selectedId || ''}
                  onChange={(e) => setSelectedId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="text-sm font-medium text-gray-700 mb-1">
                  Valeurs spécifiques à ce document
                </div>
                <div className="text-xs text-gray-500 mb-2">
                  Renseigne uniquement les champs qui ne sont pas déjà dans la fiche
                  employé (ou que tu veux écraser pour ce document).
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {OVERRIDE_FIELDS.map((f) => (
                    <div key={f.key}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {f.label}
                      </label>
                      <input
                        type={f.type}
                        value={overrides[f.key] || ''}
                        onChange={(e) =>
                          setOverrides((o) => ({ ...o, [f.key]: e.target.value }))
                        }
                        className="w-full px-2.5 py-1.5 border rounded text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {previewHtml && (
                <div className="border rounded-lg overflow-hidden h-[400px]">
                  <iframe
                    title="aperçu"
                    srcDoc={previewHtml}
                    className="w-full h-full"
                    sandbox=""
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Annuler
          </button>
          <button
            onClick={handlePreview}
            disabled={!selectedId}
            className="px-4 py-2 text-sm text-primary border border-primary rounded-lg hover:bg-primary/10 inline-flex items-center gap-1.5 disabled:opacity-40"
          >
            <Eye size={14} /> Aperçu
          </button>
          <button
            onClick={handleGenerate}
            disabled={!selectedId || generating}
            className="px-4 py-2 text-sm bg-primary text-white hover:bg-primary-dark rounded-lg inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {generating ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
            Générer le PDF
          </button>
        </div>
      </div>
    </div>
  );
}
